import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { type ENVIRONMENT, getEnv } from "@/utils/constants";

declare global {
  interface Window {
    /** Guards against double-init when Qiankun remounts the MFE. */
    __pnxCashierDatadogRumInitialized?: boolean;
    /** Guards against double-init when Qiankun remounts the MFE. */
    __pnxCashierDatadogLogsInitialized?: boolean;
  }
}

// Pepperstone Datadog standard env values (dev/stg/prd).
const ENV_TO_DD_ENV: Record<ENVIRONMENT, string> = {
  development: "dev",
  staging: "stg",
  production: "prd",
};

// Pepperstone standard: 5% session sampling in prd. Non-prd environments are
// low traffic, so sample everything there.
const SESSION_SAMPLE_RATE_BY_DD_ENV: Record<string, number> = {
  dev: 100,
  stg: 100,
  prd: 5,
};

// First-party intake proxy (pepperstone/datadog-proxy). Routing RUM + session
// replay through our own domain defeats ad blockers and corporate filters that
// drop the well-known browser-intake-datadoghq.com host — the main cause of
// silent gaps in replay/RUM data. The proxy forwards ONLY /api/v2/rum and
// /api/v2/replay; every other intake path (notably /api/v2/logs) is rejected
// with 403, so Browser Logs must stay direct (see initDatadogLogs).
const PROXY_URL_BY_DD_ENV: Record<string, string> = {
  dev: "https://datadog-proxy.dev.cloudops.pepperstone.com",
  stg: "https://datadog-proxy.stg.cloudops.pepperstone.com",
  // Deliberately a non-*.pepperstone.com domain — that suffix is exactly what
  // ad blockers drop. A failover twin (proxy.analysis.exchange) fronts the same
  // backend for China DNS-block rotation.
  prd: "https://proxy.insights.exchange",
};

const isBrowserExtensionError = (stack: string | undefined): boolean =>
  /-extension:\/\//.test(stack ?? "");

/**
 * Initialise Datadog RUM for the cashier MFE.
 *
 * No-ops when the RUM application is not provisioned for the environment
 * (missing VITE_DATADOG_APPLICATION_ID / VITE_DATADOG_CLIENT_TOKEN), and when
 * already initialised (Qiankun remount).
 */
export const initDatadogRum = () => {
  if (window.__pnxCashierDatadogRumInitialized) {
    return;
  }

  const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  if (!applicationId || !clientToken) {
    return;
  }

  const ddEnv = ENV_TO_DD_ENV[getEnv()];
  const backendUrl: string | undefined = import.meta.env.VITE_PNX_BACKURL;
  // First-party proxy for RUM + session replay (undefined for local/test/
  // ephemeral builds, where RUM also no-ops on the missing app id above).
  const proxy: string | undefined = PROXY_URL_BY_DD_ENV[ddEnv];

  try {
    datadogRum.init({
      applicationId,
      clientToken,
      site: "datadoghq.com",
      service: "pnx-cashier-web",
      env: ddEnv,
      // Must match the source-map release version uploaded at build time.
      version: import.meta.env.VITE_COMMIT_HASH,
      // Send RUM + replay intake through the first-party proxy so ad blockers
      // stop dropping it. Omitted when unset → SDK sends direct.
      ...(proxy ? { proxy: proxy } : {}),
      sessionSampleRate: SESSION_SAMPLE_RATE_BY_DD_ENV[ddEnv] ?? 100,
      sessionReplaySampleRate: 100,
      // The WebX host (public-site) runs its own RUM instance which stores its
      // session state in the shared `_dd_s` cookie. Keep our session in
      // localStorage so the two instances never share a session id or — more
      // importantly — a sampling decision (our 5% prd rate must not silence the
      // host's RUM, and their rate must not override ours). Correlation with
      // backend traces happens via the payment referenceId instead.
      sessionPersistence: "local-storage",
      // Payment flow: fully mask all text in session replay (PCI).
      defaultPrivacyLevel: "mask",
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      allowedTracingUrls: backendUrl
        ? [{ match: backendUrl, propagatorTypes: ["datadog"] }]
        : [],
      beforeSend: (event) => {
        // Drop errors raised by browser extensions — not actionable.
        if (
          event.type === "error" &&
          isBrowserExtensionError(event.error.stack)
        ) {
          return false;
        }
        return true;
      },
    });

    window.__pnxCashierDatadogRumInitialized = true;
  } catch (error) {
    console.error("Error initializing Datadog RUM:", error);
  }

  initDatadogLogs(clientToken, ddEnv);
};

/**
 * Initialise Datadog Browser Logs alongside RUM, at 100% sampling.
 *
 * RUM is sampled at 5% in prd, so frontend errors from unsampled sessions
 * would otherwise be invisible. The Logs SDK samples independently: with
 * `forwardErrorsToLogs` every uncaught exception, console.error and failed
 * network request reaches Datadog Logs regardless of RUM sampling.
 *
 * This only *forwards* errors — it does not replace any existing logging
 * (the WebX host's logging stays as-is while we verify this works).
 *
 * Logs intake is sent DIRECT (no first-party proxy): the datadog-proxy
 * forwards /api/v2/rum, /api/v2/replay and /api/v2/logs.
 */
const initDatadogLogs = (clientToken: string, ddEnv: string) => {
  if (window.__pnxCashierDatadogLogsInitialized) {
    return;
  }

  const proxy: string | undefined = PROXY_URL_BY_DD_ENV[ddEnv];

  try {
    datadogLogs.init({
      clientToken,
      site: "datadoghq.com",
      service: "pnx-cashier-web",
      env: ddEnv,
      // Must match the source-map release version uploaded at build time.
      version: import.meta.env.VITE_COMMIT_HASH,
      ...(proxy ? { proxy: proxy } : {}),
      // 100% — independent of RUM's 5% prd session sampling (see above).
      sessionSampleRate: 100,
      forwardErrorsToLogs: true,
      // Same isolation concern as RUM: the WebX host's SDK owns the shared
      // `_dd_s` cookie, so keep our session state in localStorage.
      sessionPersistence: "local-storage",
      beforeSend: (log) => {
        // Drop errors raised by browser extensions — not actionable.
        if (isBrowserExtensionError(log.error?.stack)) {
          return false;
        }
        return true;
      },
    });

    window.__pnxCashierDatadogLogsInitialized = true;
  } catch (error) {
    console.error("Error initializing Datadog Logs:", error);
  }
};

/**
 * Attach the current flow correlation id (sent to the backend as
 * X-Correlation-ID on every request) to all subsequent RUM events, so RUM
 * sessions and backend logs are joinable via the same `@correlation_id`.
 */
export const setFlowCorrelation = (correlationId: string) => {
  datadogRum.setGlobalContextProperty("correlation_id", correlationId);
  datadogLogs.setGlobalContextProperty("correlation_id", correlationId);
};

/**
 * Attach our transaction identifiers to all subsequent RUM events so Nuvei
 * SDK errors can be correlated with backend traces.
 */
export const setPaymentCorrelation = (correlation: {
  referenceId?: string;
  orderId?: string | number;
}) => {
  const payment = {
    referenceId: correlation.referenceId,
    orderId: correlation.orderId,
  };
  datadogRum.setGlobalContextProperty("payment", payment);
  datadogLogs.setGlobalContextProperty("payment", payment);
};
